import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react"
import {
  ArrowsRightLeftIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
} from "@heroicons/react/20/solid"

import type { SolarDesignVersionsDto } from "~/api/dto/solar-design-versions.dto"

import { versionLabel } from "../SolarDesignVersions"

const itemClass = `flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-foreground data-disabled:cursor-not-allowed data-disabled:opacity-50 data-focus:bg-surface`

/**
 * The V2 designer's version chip: "Ver. N · name" for the design being
 * edited, with Duplicate this version and Switch version…. It replaces V1's
 * design switcher, which stays off on this route.
 */
export const SolarDesignVersionChip = ({
  versions,
  designId,
  busy,
  onDuplicate,
  onSwitch,
}: {
  versions: SolarDesignVersionsDto | undefined
  /** The design the designer is editing. */
  designId: string
  busy: boolean
  onDuplicate: () => void
  onSwitch: () => void
}): JSX.Element | null => {
  const version = versions?.versions.find(({ id }) => id === designId)
  if (!versions || !version) return null
  // The panel's rule: a design-team version still in review, or rejected, isn't copied.
  const duplicateBlocked =
    versions.switchBlockedReason ??
    (version.requestState === `rejected` ||
    version.requestState === `in_progress`
      ? version.activateBlockedReason
      : null)

  return (
    <Menu>
      <MenuButton
        data-testid="solar-design-version-chip"
        className="inline-flex max-w-64 items-center gap-1.5 rounded-full border border-border bg-surface px-3 py-1 text-sm font-semibold whitespace-nowrap text-foreground hover:bg-surface-elevated-strong"
      >
        <span className="truncate">
          {busy ? `Duplicating…` : versionLabel(version)}
        </span>
        <ChevronDownIcon aria-hidden="true" className="size-4 shrink-0" />
      </MenuButton>
      <MenuItems
        anchor="bottom start"
        className="z-[2147483002] w-64 rounded-xl border border-border bg-background p-1 shadow-lg [--anchor-gap:6px] focus:outline-none"
      >
        <MenuItem disabled={busy || duplicateBlocked !== null}>
          <button type="button" className={itemClass} onClick={onDuplicate}>
            <DocumentDuplicateIcon
              aria-hidden="true"
              className="size-4 text-muted"
            />
            Duplicate this version
          </button>
        </MenuItem>
        <MenuItem disabled={busy}>
          <button type="button" className={itemClass} onClick={onSwitch}>
            <ArrowsRightLeftIcon
              aria-hidden="true"
              className="size-4 text-muted"
            />
            Switch version…
          </button>
        </MenuItem>
        {duplicateBlocked ? (
          <p className="px-3 py-2 text-xs text-muted">{duplicateBlocked}</p>
        ) : null}
      </MenuItems>
    </Menu>
  )
}
